import type { ReactNode } from "react"
import { useEffect, useRef } from "react"
import { useLocation } from "@tanstack/react-router"
import { create } from "zustand" // move to lib/states
import { createJSONStorage, persist } from "zustand/middleware"

import { cn } from "@productlane/lib/src/browser"
import { ScrollArea, ScrollViewport } from "@productlane/ui"

interface DetailScreenProps {
	children?: ReactNode
}

type DetailsScreenLayoutProps = DetailScreenProps & { className?: string }

interface DetailsPageState {
	scrollByPath: Record<string, number>
	setScrollPosition: (path: string, value: number) => void
}

export const useDetailsPageState = create<DetailsPageState>()(
	persist(
		(set) => ({
			scrollByPath: {},
			setScrollPosition: (path, value) => {
				set((state) => ({
					scrollByPath: { ...state.scrollByPath, [path]: value },
				}))
			},
		}),
		{
			name: "details-page-state",
			storage: createJSONStorage(() => sessionStorage),
		},
	),
)

function DetailScreenLayout({ children, className }: DetailsScreenLayoutProps) {
	return (
		<div
			className={cn(
				"grid flex-1 grid-cols-12 grid-rows-1 overflow-hidden",
				className,
			)}
		>
			{children}
		</div>
	)
}

interface DetailsScreenEditorProps extends DetailScreenProps {
	className?: string
	hasOverflow?: boolean
	isPadded?: boolean
	scrollKey?: string
	contentClassName?: string
}

function DetailScreenEditor({
	children,
	className,
	hasOverflow = true,
	isPadded = true,
	scrollKey,
	contentClassName,
}: DetailsScreenEditorProps) {
	const scrollRef = useRef<HTMLDivElement | null>(null)
	const location = useLocation()
	const pathname = scrollKey ?? location.pathname
	const { scrollByPath, setScrollPosition } = useDetailsPageState()

	useEffect(() => {
		const target = scrollByPath[pathname] ?? 0
		let frame = 0
		let attempts = 0
		const restore = () => {
			const el = scrollRef.current
			if (!el) return
			el.scrollTo({ top: target, behavior: "instant" })
			attempts += 1
			if (Math.abs(el.scrollTop - target) > 1 && attempts < 30) {
				frame = requestAnimationFrame(restore)
			}
		}
		frame = requestAnimationFrame(restore)
		return () => cancelAnimationFrame(frame)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname])
	return (
		<div className={cn("col-span-12 w-full", className)}>
			{hasOverflow ? (
				<ScrollArea>
					<ScrollViewport
						ref={scrollRef}
						onScroll={(e) => {
							const target = e.target as HTMLElement
							setScrollPosition(pathname, target.scrollTop)
						}}
					>
						<div
							className={cn(
								"mx-auto flex min-h-full max-w-[62rem] flex-col",
								isPadded ? "px-4 py-7 md:px-8" : "",
								contentClassName,
							)}
						>
							{children}
						</div>
					</ScrollViewport>
				</ScrollArea>
			) : (
				<div
					className={cn(
						"mx-auto flex h-full max-w-full flex-col",
						isPadded ? "p-10" : "",
						contentClassName,
					)}
				>
					{children}
				</div>
			)}
		</div>
	)
}

interface DetailsScreenIssueSidebarProps extends DetailScreenProps {
	className?: string
}

function DetailScreenIssueSidebar({
	children,
	className,
}: DetailsScreenIssueSidebarProps) {
	return (
		<div
			className={cn(
				"col-span-12 mx-4 h-full py-7 pb-12 md:px-10 lg:col-span-5 lg:mx-0 lg:border-l",
				className,
			)}
		>
			{children}
		</div>
	)
}

export { DetailScreenEditor, DetailScreenLayout, DetailScreenIssueSidebar }
export {
	DetailThreadsList,
	hasActiveDetailFilters,
	useDetailThreadsControls,
} from "./detail-screen/detail-threads-list"
